import { Typography } from "antd"
import React from "react"

const { Text } = Typography

/**
 * Splits a field's already-defaulted display string into the lines it
 * should render as. A real `\n` — the shape `payment_terms`/`scope` take on
 * the Proposal/Contract side once the backend can name real payment
 * milestones or phases — always wins and applies to any field, not just
 * those two. Absent a `\n`, only `scope` falls back to splitting on `", "`:
 * Lead's own `scope` value is raw free text with no such guarantee, but in
 * current demo data it holds a comma-joined string written by the
 * Milestones feature (e.g. `"m1, m2"`). No other field gets this fallback —
 * an address or a name can legitimately contain a comma that is not a list
 * separator.
 */
function splitFieldDisplayLines(value: string, fieldKey: string): string[] {
  if (value.includes("\n")) return value.split("\n")
  if (fieldKey === "scope" && value.includes(", ")) return value.split(", ")
  return [value]
}

interface FieldValueLinesProps {
  /**
   * "Lead" / "Proposal" / "Contract" — prefixed onto the value as
   * "Label: value". Omit it for a context that already names the source
   * elsewhere (a table's own column header), in which case only the value
   * renders.
   */
  label?: string
  value: string
  fieldKey: string
  /**
   * "inline" (default) is the original "Label: value" row used by the
   * Generate confirm modals. "bulleted" drops the label and renders each
   * line as its own `<li>` — for a comparison table cell, where a flowing
   * paragraph would make a multi-phase scope or multi-milestone payment
   * schedule unreadable.
   */
  variant?: "inline" | "bulleted"
}

/**
 * Renders one side of a field-comparison row. The single-line case renders
 * as plain text (prefixed "Label: value" when `label` is given, unchanged
 * from before this component existed). When `splitFieldDisplayLines` finds
 * more than one line, `variant` decides the shape: "inline" keeps the label
 * standing alone with each line below it as its own row; "bulleted" renders
 * every line as an `<li>` with no label, for use inside a table cell whose
 * column header already says which source this is.
 */
function FieldValueLines({
  label,
  value,
  fieldKey,
  variant = "inline"
}: FieldValueLinesProps) {
  const lines = splitFieldDisplayLines(value, fieldKey)

  if (variant === "bulleted") {
    if (lines.length <= 1) {
      return <span>{value}</span>
    }

    return (
      <ul className="list-disc pl-4 m-0 space-y-0.5">
        {lines.map((line, index) => (
          <li key={`${index}-${line}`}>{line}</li>
        ))}
      </ul>
    )
  }

  if (lines.length <= 1) {
    return (
      <Text type={"secondary"}>
        {label}: {value}
      </Text>
    )
  }

  return (
    <div>
      <Text type={"secondary"}>{label}:</Text>
      {lines.map((line, index) => (
        <div key={`${index}-${line}`}>
          <Text type={"secondary"}>{line}</Text>
        </div>
      ))}
    </div>
  )
}

export { FieldValueLines, splitFieldDisplayLines }
